import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../../firebase';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { channelSessionDocId } from '../../utils/videoCallFirestore';

export type ChatMediaPayload = {
  url: string;
  filename: string;
  size: number;
  contentType: string;
};

export type ChatMessageRow =
  | {
      sid: string;
      author: string;
      body: string;
      dateCreated: Date;
      type: 'text';
    }
  | {
      sid: string;
      author: string;
      dateCreated: Date;
      type: 'media';
      media: ChatMediaPayload;
    };

type ChatContextType = {
  isChatWindowOpen: boolean;
  setIsChatWindowOpen: (isChatWindowOpen: boolean) => void;
  connect: (token: string) => void;
  hasUnreadMessages: boolean;
  messages: ChatMessageRow[];
  conversation: { sendMessage: (body: string | FormData) => Promise<void> } | null;
};

export const ChatContext = createContext<ChatContextType>(null!);

export const ChatProvider: React.FC = ({ children }) => {
  const [isChatWindowOpen, setIsChatWindowOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageRow[]>([]);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const { room } = useVideoContext();

  const channelId = room?.channelName ? channelSessionDocId(room.channelName) : null;

  useEffect(() => {
    if (!channelId) {
      setMessages([]);
      setHasUnreadMessages(false);
      return;
    }
    // New meeting / channel: wipe UI immediately so the previous room's chat does not linger
    // until the new onSnapshot runs (or flash if the new room is empty).
    setMessages([]);
    setHasUnreadMessages(false);

    const ref = collection(db, 'video_call_sessions', channelId, 'chat_messages');
    const q = query(ref, orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      snap => {
        const rows: ChatMessageRow[] = snap.docs.map(d => {
          const data = d.data();
          const created = data.createdAt?.toDate?.() ?? new Date();
          const t = data.type === 'media' ? 'media' : 'text';
          if (t === 'media') {
            return {
              sid: d.id,
              author: String(data.author ?? 'User'),
              dateCreated: created,
              type: 'media' as const,
              media: {
                url: String(data.mediaUrl ?? ''),
                filename: String(data.filename ?? 'file'),
                size: Number(data.size ?? 0),
                contentType: String(data.contentType ?? 'application/octet-stream'),
              },
            };
          }
          return {
            sid: d.id,
            author: String(data.author ?? 'User'),
            body: String(data.body ?? ''),
            dateCreated: created,
            type: 'text' as const,
          };
        });
        setMessages(rows);
      },
      err => console.error('chat_messages:', err)
    );
    return () => unsub();
  }, [channelId]);

  useEffect(() => {
    if (isChatWindowOpen) {
      setHasUnreadMessages(false);
    }
  }, [isChatWindowOpen]);

  const conversation = useMemo(() => {
    if (!channelId || !room) return null;
    const localName = room.localParticipant?.identity ?? 'Me';

    return {
      sendMessage: async (body: string | FormData) => {
        const col = collection(db, 'video_call_sessions', channelId, 'chat_messages');

        if (body instanceof FormData) {
          const raw = body.get('userfile');
          const file = raw instanceof Blob ? raw : null;
          if (!file) {
            throw new Error('No file selected');
          }
          if (!auth.currentUser) {
            console.warn('chat media: Firebase Auth has no user; Storage rules may reject uploads.');
          }
          const safeName =
            (raw instanceof File && raw.name
              ? raw.name
              : 'upload'
            ).replace(/[^a-zA-Z0-9._-]/g, '_') || 'upload';
          const path = `chat/${channelId}/${Date.now()}_${safeName}`;
          const sref = ref(storage, path);
          try {
            await uploadBytes(sref, file, { contentType: file.type || 'application/octet-stream' });
            const url = await getDownloadURL(sref);
            const displayName = raw instanceof File && raw.name ? raw.name : safeName;
            await addDoc(col, {
              author: localName,
              type: 'media',
              mediaUrl: url,
              filename: displayName,
              size: file.size,
              contentType: file.type || 'application/octet-stream',
              createdAt: serverTimestamp(),
            });
          } catch (e) {
            console.error('chat media upload failed:', e);
            throw e;
          }
          return;
        }

        const text = String(body).trim();
        if (!text) return;
        await addDoc(col, {
          author: localName,
          body: text,
          type: 'text',
          createdAt: serverTimestamp(),
        });
      },
    };
  }, [channelId, room]);

  const connect = useCallback((_token: string) => {
    /* legacy Twilio hook — no-op for Agora */
  }, []);

  return (
    <ChatContext.Provider
      value={{
        isChatWindowOpen,
        setIsChatWindowOpen,
        connect,
        hasUnreadMessages,
        messages,
        conversation,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
